import React, { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Search, Loader2, CheckCircle2, Package, Truck, Clock, XCircle } from "lucide-react";
import { fmt } from "@/data/packs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { key: "paid", label: "Paiement confirmé", icon: CheckCircle2 },
  { key: "en préparation", label: "En préparation", icon: Package },
  { key: "expédiée", label: "Expédiée", icon: Truck },
];

export default function OrderTracking() {
  const [params] = useSearchParams();
  const [code, setCode] = useState(params.get("order") || "");
  const [order, setOrder] = useState(null);
  const [state, setState] = useState("idle"); // idle | loading | found | notfound | error
  const [error, setError] = useState("");

  const lookup = useCallback(async (value) => {
    const oc = (value || "").trim();
    if (!oc) return;
    setState("loading");
    setError("");
    try {
      const { data } = await axios.get(`${API}/orders/track/${encodeURIComponent(oc)}`);
      setOrder(data);
      setState("found");
    } catch (e) {
      if (e.response && e.response.status === 404) {
        setState("notfound");
      } else {
        setState("error");
        setError("Une erreur est survenue. Merci de réessayer.");
      }
    }
  }, []);

  useEffect(() => {
    if (params.get("order")) lookup(params.get("order"));
  }, [params, lookup]);

  const paid = order?.payment_status === "paid";
  const fulfil = order?.fulfillment_status;
  const activeIndex = !paid ? -1 : fulfil === "expédiée" ? 2 : 1;

  return (
    <div className="px-4 sm:px-8 lg:px-16 max-w-2xl mx-auto py-12 md:py-16">
      <Link to="/" className="inline-flex items-center gap-2 text-sm text-[var(--oz-muted)] hover:text-[var(--oz-text)] mb-8">
        <ArrowLeft className="w-4 h-4" /> Retour à l'accueil
      </Link>
      <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] text-[var(--oz-gold)] mb-3">
        Suivi de commande
      </span>
      <h1 className="font-serif text-4xl font-medium tracking-tight text-[var(--oz-text)]">Suivre ma commande</h1>
      <p className="mt-3 text-sm text-[var(--oz-muted)]">
        Saisissez votre numéro de commande (ex. OZ-XXXXXXXX) reçu par email.
      </p>

      <div className="mt-6 flex gap-2">
        <input
          data-testid="track-input"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && lookup(code)}
          placeholder="OZ-XXXXXXXX"
          className="flex-1 rounded-full border border-[var(--oz-border)] px-5 py-3.5 text-sm outline-none focus:border-[var(--oz-gold)] bg-white"
        />
        <button
          data-testid="track-submit"
          onClick={() => lookup(code)}
          disabled={state === "loading"}
          className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-[var(--oz-dark)] text-white font-semibold text-sm hover:bg-black transition-colors disabled:opacity-60"
        >
          {state === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          Suivre
        </button>
      </div>

      {state === "notfound" && (
        <div data-testid="track-notfound" className="mt-6 bg-white border border-[var(--oz-border)] rounded-2xl p-6 flex items-center gap-3">
          <XCircle className="w-6 h-6 text-[var(--oz-badge)]" />
          <p className="text-sm text-[var(--oz-muted)]">Aucune commande trouvée pour ce numéro. Vérifiez la saisie.</p>
        </div>
      )}
      {state === "error" && <p className="mt-6 text-sm text-[var(--oz-badge)]">{error}</p>}

      {state === "found" && order && (
        <div data-testid="track-result" className="mt-8 bg-white border border-[var(--oz-border)] rounded-[1.5rem] p-6 md:p-8">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="text-xs uppercase tracking-wider text-[var(--oz-muted)]">Commande</div>
              <div className="font-serif text-2xl text-[var(--oz-text)]">{order.order_number}</div>
            </div>
            <span className={`text-xs font-semibold px-3 py-1.5 rounded-full ${paid ? "bg-[var(--oz-nude)] text-[var(--oz-badge)]" : "bg-[var(--oz-bg-2)] text-[var(--oz-muted)]"}`}>
              {paid ? "Payée" : "En attente de paiement"}
            </span>
          </div>

          {!paid ? (
            <div className="mt-6 flex items-center gap-2 text-sm text-[var(--oz-muted)]">
              <Clock className="w-4 h-4" /> Le paiement de cette commande n'a pas encore été confirmé.
            </div>
          ) : (
            <div className="mt-8 flex justify-between relative">
              <div className="absolute top-5 left-0 right-0 h-0.5 bg-[var(--oz-border)]" />
              {STEPS.map((s, i) => {
                const done = i <= activeIndex;
                return (
                  <div key={s.key} className="relative flex flex-col items-center gap-2 flex-1">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${done ? "bg-[var(--oz-gold)] border-[var(--oz-gold)] text-white" : "bg-white border-[var(--oz-border)] text-[var(--oz-muted)]"}`}>
                      <s.icon className="w-4 h-4" />
                    </div>
                    <span className={`text-[11px] text-center leading-tight ${done ? "text-[var(--oz-text)] font-semibold" : "text-[var(--oz-muted)]"}`}>
                      {s.label}
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          <div className="mt-8 border-t border-[var(--oz-border)] pt-5 space-y-2">
            {order.items?.map((it, i) => (
              <div key={i} className="flex justify-between text-sm">
                <span className="text-[var(--oz-text)]">{it.name} ({it.products} produits) x{it.quantity}</span>
              </div>
            ))}
            <div className="flex justify-between text-sm font-semibold pt-2 border-t border-[var(--oz-border)]">
              <span>Total payé</span>
              <span>{order.amount != null ? fmt(order.amount) : "—"}</span>
            </div>
            <div className="flex justify-between text-sm text-[var(--oz-muted)]">
              <span>Livraison</span>
              <span className="text-[var(--oz-gold)] font-semibold">Offerte • suivie</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
